import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ContentCompetency } from '@backrs_ui/common-ui';

import { Box, SxProps, Theme } from '@mui/material';

import { getProsperPageView } from './prosper.util';
import { useAgentContext } from './ProsperPage.agentContext';
import { ProsperContent } from '../molecules/ProsperContent';
import { ProsperHeader } from '../molecules/ProsperHeader';
import { ProsperSubHeader } from '../molecules/ProsperSubHeader';

type ProsperPageProps = Readonly<{
  contentCompetencies: ContentCompetency[];
}>;

export const ProsperPage = ({ contentCompetencies }: ProsperPageProps) => {
  const [searchParams] = useSearchParams();
  const unlockParam = searchParams.get('unlock');

  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    unlockParam ? [unlockParam] : [],
  );
  const [isAllCategoriesSelected, setIsAllCategoriesSelected] = useState(!unlockParam);

  const filteredCompetencies = contentCompetencies
    .filter((competency) => {
      // do not show any competencies that don't have an associated unlock
      if (competency.unlock == null) return false;

      // filter by category if the user any are selected
      const matchesCategory =
        isAllCategoriesSelected ||
        selectedCategories.length === 0 ||
        selectedCategories.includes(competency.unlock.slug);

      return matchesCategory;
    })
    .sort((a, b) => a.sequence - b.sequence);

  const pageView = getProsperPageView(isAllCategoriesSelected, selectedCategories);

  useAgentContext({
    selectedCategories,
    filteredCompetencyNames: filteredCompetencies.map((c) => c.title),
    pageView,
  });

  // toggle category filter
  const toggleCategory = (categorySlug: string) => {
    setSelectedCategories((prev) =>
      prev.includes(categorySlug)
        ? prev.filter((c) => c !== categorySlug)
        : [...prev, categorySlug],
    );
    setIsAllCategoriesSelected(false);
  };

  // toggle all categories filter
  const toggleAllCategories = () => {
    setSelectedCategories([]);
    setIsAllCategoriesSelected(true);
  };

  return (
    <Box sx={parentContainerStyle}>
      <ProsperHeader />
      <ProsperSubHeader
        isAllCategoriesSelected={isAllCategoriesSelected}
        selectedCategories={selectedCategories}
        onToggleAllCategories={toggleAllCategories}
        onToggleCategory={toggleCategory}
      />
      <ProsperContent contentCompetencies={filteredCompetencies} pageView={pageView} />
    </Box>
  );
};

const parentContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  gap: backrs.spacing['spacing-2xl'],
});
